import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';

/**
 * Community hub: Societies, Posts, and other community-related sections.
 * Presentational hub linking to /societies and /posts; matches Explore page pattern.
 */
const COMMUNITY_OPTIONS = [
  {
    id: 'societies',
    title: 'Societies',
    description: 'Discover residential societies, join your community, and stay updated with local news and events.',
    path: '/societies',
    label: 'Browse Societies',
  },
  {
    id: 'posts',
    title: 'Posts',
    description: 'Read and share posts from your community — updates, discussions, and local happenings.',
    path: '/posts',
    label: 'View Posts',
  },
];

const CommunityPage = () => {
  return (
    <PageLayout title="Community - PakPoints">
      <Helmet>
        <title>Community - PakPoints</title>
        <meta name="description" content="Connect with your community: societies, posts, and local discussions on PakPoints." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero strip: consistent with Explore / home theme */}
        <section
          className="relative py-12 sm:py-16 overflow-hidden"
          aria-label="Community overview"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50" aria-hidden="true" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-100/30 via-transparent to-transparent" aria-hidden="true" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Community
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Societies and posts — connect with your area and join the conversation.
            </p>
          </div>
        </section>

        {/* Option cards */}
        <section
          className="pb-16 sm:pb-20"
          aria-labelledby="community-options-heading"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="community-options-heading" className="sr-only">
              Community by category
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {COMMUNITY_OPTIONS.map((option) => (
                <Link
                  key={option.id}
                  to={option.path}
                  className="group rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                >
                  <div
                    className="w-14 h-14 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center text-2xl font-bold group-hover:bg-primary-200 transition-colors"
                    aria-hidden="true"
                  >
                    {option.title.charAt(0)}
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {option.title}
                  </h3>
                  <p className="mt-2 text-gray-600 flex-1">
                    {option.description}
                  </p>
                  <span className="mt-6 inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700">
                    {option.label}
                    <svg className="ml-1.5 w-4 h-4 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default CommunityPage;
